/* eslint-disable @typescript-eslint/no-unused-vars */
// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { getErrorKey } from "@/Common/helpers/Modifiers/characters";

const CONSTANTS = {
	MAX_GROSS_INPUT: 99999.99,
	MAX_COLUMNS_INPUT: 9999,
	MAX_ROWS_INPUT: 9999.99,
	MIN_VOLUME_INPUT: 0,
	SMALLEST_TIER_AMOUNT: 10000,
	DEFAULT_BACKEND_PRODUCTS: 10,
	EXCLUDED_TOP_SWITCHES: ["finance"], // excludeFromTopSwitches
	TAB_SETTINGS: ["combineFranchises", "globalUnitCounts", "newAndPreOwnedRequired", "combineOEMs"], // tabSettings
	POTENTIAL_KEYS: ["newVehicles", "preOwned", "finance"], // potentialKeys
	EXCLUDED_FROM_COMBINE: ["preOwned", "finance"], // excludeFromCombineTabs,
	NEW_AND_PRE_OWNED_REQUIRED_LABEL: "New and Pre-Owned Required",
};

const isDifferentDimensions = (matrix, target) =>
	matrix.maxTiers !== target.tiers ||
	matrix.minTiers !== target.tiers ||
	matrix.maxColumns !== target.columns ||
	matrix.minColumns !== target.columns;

const expandTierRows = (args) => {
	const { tiers = [], maxRows = 1, sectionId, make } = args;
	const expandedMakes = [];

	while (tiers?.length < maxRows) {
		const newTier = clone(tiers[tiers.length - 1]);
		newTier.from.value = CONSTANTS.MAX_GROSS_INPUT + 0.01;
		newTier.commissions.forEach((commission) => {
			commission.commission.value = 0;
		});
		tiers?.push(newTier);
		expandedMakes.push(getErrorKey(sectionId, make));
	}

	return {
		tiers,
		expandedMakes,
	};
};

const expandTiersColumns = (args) => {
	const { tiers = [], maxColumns = 1, sectionId, make } = args;
	const expandedMakes = [];

	if (tiers.length < maxColumns) {
		tiers[tiers.length - 1].to.value = CONSTANTS.MIN_VOLUME_INPUT;
		const newTier = clone(tiers[tiers.length - 1]);
		newTier.from.value = CONSTANTS.MIN_VOLUME_INPUT;
		newTier.commission.value = 0;

		while (tiers?.length < maxColumns) {
			tiers.push(clone(newTier));
		}

		expandedMakes.push(getErrorKey(sectionId, make?.make));
	}

	return {
		tiers,
		expandedMakes,
	};
};

const expandMakes = (args) => {
	const { makes = [], maxTiers = 1, maxColumns = 1, sectionId, maximumColumnsInput } = args;
	let expandedKeys = [];

	for (const make of makes) {
		if (make?.tiers?.length < maxTiers) {
			make.tiers[make.tiers.length - 1].to.value = CONSTANTS.MAX_GROSS_INPUT;

			const { tiers, expandedMakes: expandedRows } = expandTierRows({
				tiers: make.tiers,
				maxRows: maxTiers,
				make: make?.make || "",
				sectionId,
			});

			make.tiers = [...tiers];
			expandedKeys = [...expandedKeys, ...expandedRows];

			make.tiers[make.tiers.length - 1].to.value = CONSTANTS.MAX_GROSS_INPUT;
		}

		for (const columnTiers of make?.tiers || []) {
			const { tiers: rowsTiers, expandedMakes: expandedColumns } = expandTiersColumns({
				tiers: columnTiers?.commissions,
				make: make?.make || "",
				maxColumns,
				sectionId,
			});

			columnTiers.commissions = [...rowsTiers];
			columnTiers.commissions[columnTiers?.commissions.length - 1].to.value = maximumColumnsInput;

			expandedKeys = [...expandedKeys, ...expandedColumns];
		}
	}

	return {
		makes,
		expandedKeys,
	};
};

const shrinkMakes = (args) => {
	let { makes = [], maxTiers = 1, maxColumns = 1, maximumColumnsInput } = args;

	const shrunk = clone(makes);
	for (const make of shrunk) {
		// Reducing rows tiers
		make.tiers.length = maxTiers;
		while (!make.tiers[maxTiers - 1]) {
			maxTiers--;
		}

		make.tiers[maxTiers - 1].to.value = CONSTANTS.MAX_GROSS_INPUT;

		for (const tiers of make?.tiers || []) {
			const commissionTiers = tiers?.commissions;

			// Reducing columns tiers
			if (tiers && commissionTiers?.length > maxColumns) {
				commissionTiers.length = maxColumns;
				commissionTiers[maxColumns - 1].to.value = maximumColumnsInput;
			}
		}
	}

	return {
		makes: shrunk,
	};
};

const getMatrixDimensionsForTab = (tab) => {
	const makes = tab?.makes || [];

	let smallestColumnsAmount = CONSTANTS.SMALLEST_TIER_AMOUNT;
	let smallestTiersAmount = CONSTANTS.SMALLEST_TIER_AMOUNT;
	let largestColumnsAmount = 0;
	let largestTiersAmount = 0;

	makes.forEach((make) => {
		smallestTiersAmount = Math.min(smallestTiersAmount, make?.tiers?.length);
		smallestColumnsAmount = Math.min(smallestColumnsAmount, make?.tiers?.[0]?.commissions?.length);
		largestTiersAmount = Math.max(largestTiersAmount, make?.tiers?.length);
		largestColumnsAmount = Math.max(largestColumnsAmount, make?.tiers?.[0]?.commissions?.length);
	});

	return {
		minTiers: smallestTiersAmount,
		minColumns: smallestColumnsAmount,
		maxTiers: largestTiersAmount,
		maxColumns: largestColumnsAmount,
	};
};

const getMatrixDimensions = (tabs, keysToExclude = []) => {
	const matrixDimensions = new Map();
	tabs.forEach((tab) => {
		const key = tab?.key;
		if (!keysToExclude.includes(key)) {
			matrixDimensions.set(key, getMatrixDimensionsForTab(tab));
		}
	});
	return matrixDimensions;
};

const getExactMatrixDimensions = (tiers) => {
	let columnsCount = 0;
	let tiersCount = 0;
	columnsCount = tiers[0]?.commissions?.length;
	tiersCount = tiers.length;
	return {
		columns: columnsCount,
		tiers: tiersCount,
	};
};

const getSmallestMatrixDimensions = (matrixDimensions) => {
	let columns = CONSTANTS.SMALLEST_TIER_AMOUNT;
	let tiers = CONSTANTS.SMALLEST_TIER_AMOUNT;

	for (const [key, dimensions] of matrixDimensions) {
		if (!CONSTANTS.EXCLUDED_TOP_SWITCHES.includes(key)) {
			columns = Math.min(columns, dimensions?.minColumns);
			tiers = Math.min(tiers, dimensions?.minTiers);
		}
	}

	if (columns === CONSTANTS.SMALLEST_TIER_AMOUNT) {
		columns = 1;
	}
	if (tiers === CONSTANTS.SMALLEST_TIER_AMOUNT) {
		tiers = 1;
	}

	return {
		columns,
		tiers,
	};
};

const resizeTabs = (tabs, targetDimensions, maximumColumnsInput) => {
	tabs.forEach((tab) => {
		if (!CONSTANTS.EXCLUDED_TOP_SWITCHES.includes(tab?.key)) {
			const matrixDimensionsFromTab = getMatrixDimensionsForTab(tab);
			if (isDifferentDimensions(matrixDimensionsFromTab, targetDimensions)) {
				const { makes: shrunkMakes } = shrinkMakes({
					makes: tab.makes,
					maxTiers: targetDimensions?.tiers,
					maxColumns: targetDimensions?.columns,
					maximumColumnsInput,
				});
				tab.makes = shrunkMakes;
			}
		}
	});
	return tabs;
};

const isSomeOEMChecked = (combinedOEMsGrossStructure = []) => combinedOEMsGrossStructure?.some((entry) => entry?.checked);

const isNotOEMChecked = (args) => {
	const { isVGNewFinance, combinedOEMsGrossStructure } = args;

	const isOEMChecked = isSomeOEMChecked(combinedOEMsGrossStructure);
	return isVGNewFinance && combinedOEMsGrossStructure && !isOEMChecked;
};

const isDisabledCombineOEMs = (args) => {
	const { isSingleFranchise, isFinanceNewAndPreOwnedRequired, isGlobalUnitCounts, isVGNewFinance, combinedOEMsGrossStructure } = args;

	if (isSingleFranchise || isFinanceNewAndPreOwnedRequired || isGlobalUnitCounts || !isVGNewFinance) {
		return true;
	}

	return isNotOEMChecked({ isVGNewFinance, combinedOEMsGrossStructure });
};

const isDisabledSwitch = (args) => {
	const { readOnly, loading, financeTab, franchises, switchKey = "" } = args;

	const isDisabledOnDemand = readOnly || loading;
	const isSingleFranchise = franchises?.length < 2;
	const isVGNewFinance = financeTab?.variableGrossStructure?.newFinance;
	const isVGPreOwnedFinance = financeTab?.variableGrossStructure?.preOwnedFinance;
	const isMissingVG = !(isVGNewFinance || isVGPreOwnedFinance);

	const isFinanceNewAndPreOwnedRequired = financeTab?.newAndPreOwnedRequired;
	const isGlobalUnitCounts = financeTab?.globalUnitCounts;

	if (isDisabledOnDemand) {
		return true;
	}

	switch (switchKey) {
		case "combineOEMs":
			return isDisabledCombineOEMs({
				isSingleFranchise,
				isFinanceNewAndPreOwnedRequired,
				isGlobalUnitCounts,
				isVGNewFinance,
				combinedOEMsGrossStructure: financeTab?.combinedOEMsGrossStructure,
			});
		case "globalUnitCounts":
			return isFinanceNewAndPreOwnedRequired || isMissingVG;
		case "newAndPreOwnedRequired":
			return (
				isMissingVG ||
				isGlobalUnitCounts ||
				isNotOEMChecked({ isVGNewFinance, combinedOEMsGrossStructure: financeTab?.combinedOEMsGrossStructure })
			);

		default:
			return true;
	}
};

const getDefaultCommissionsFromModel = (model, franchises = []) => {
	const newAndPreOwnedRequired = Boolean(model?.tabsProps?.newAndPreOwnedRequired) || false;
	const combineFranchises = Boolean(model?.tabsProps?.combineFranchises) || false;
	const globalUnitCounts = Boolean(model?.tabsProps?.globalUnitCounts) || false;
	const isGuaranteedDraw = Boolean(model?.tabsProps?.isGuaranteedDraw) || false;

	const objects = {};
	const tabs = clone(model?.tabs);

	tabs?.forEach((tab) => {
		const obj = clone(tab);
		const key = tab?.key;
		delete obj.key;
		delete obj.tabName;
		objects[key] = obj;
	});

	Object.entries(objects).forEach(([key, value]) => {
		if (key !== "finance" && key !== "newVehicles" && objects[key]?.makes?.length < 2) {
			const makeInfo = clone(value["makes"][0]);
			delete value.makes;
			objects[key] = {
				...objects[key],
				...makeInfo,
			};
		}
	});

	if (!combineFranchises) {
		const variableGrossStructure = model.tabs.find((tab) => tab?.key === "newVehicles")?.variableGrossStructure || null;
		const makes = [];
		Object.entries(objects).forEach(([key, value]) => {
			if (franchises.some((franchise) => String(franchise)?.toLowerCase() === String(key).toLowerCase())) {
				makes.push(value);
				delete objects[key];
			}
		});
		objects["newVehicles"] = {
			makes,
			variableGrossStructure,
		};
	}

	// eslint-disable-next-line no-unused-vars
	Object.entries(objects).forEach(([_, value]) => {
		if (value?.makes?.length) {
			value.makes?.forEach((make) => {
				delete make?.title;
			});
		}
	});

	if (objects?.["preOwned"]) {
		delete objects["preOwned"]?.variableGrossStructure;
	}

	return {
		...objects,
		newAndPreOwnedRequired,
		combineFranchises,
		globalUnitCounts,
		isGuaranteedDraw,
	};
};

export {
	CONSTANTS,
	expandMakes,
	shrinkMakes,
	isDifferentDimensions,
	isDisabledSwitch,
	getDefaultCommissionsFromModel,
	getMatrixDimensions,
	getMatrixDimensionsForTab,
	getExactMatrixDimensions,
	getSmallestMatrixDimensions,
	resizeTabs,
};
